import { useState, useEffect, useRef } from "react";
import { initializeApp } from "firebase/app";
import { getFirestore, collection, addDoc, onSnapshot, query, orderBy } from "firebase/firestore";

const firebaseConfig = {
  apiKey: "AIzaSyAkT3VXNPsrdmgA9uWvrWPis7KiqgNHZPE",
  authDomain: "ask-a-nurse-2619b.firebaseapp.com",
  projectId: "ask-a-nurse-2619b",
  storageBucket: "ask-a-nurse-2619b.firebasestorage.app",
  messagingSenderId: "306855665527",
  appId: "1:306855665527:web:ac6462079e3ad368cc8b72"
};

const firebaseApp = initializeApp(firebaseConfig);
const db = getFirestore(firebaseApp);

// ─── FONTS ────────────────────────────────────────────────────────
const FONT_LINK = "https://fonts.googleapis.com/css2?family=Bitter:wght@400;600;700;900&family=DM+Sans:wght@300;400;500;600&display=swap";

// ─── DESIGN TOKENS ───────────────────────────────────────────────
const C = {
  navy:       "#0A1628",
  navyMid:    "#152240",
  navyLight:  "#1E3A5F",
  teal:       "#00C9A7",
  tealDim:    "#00A589",
  tealGlow:   "rgba(0,201,167,0.15)",
  white:      "#FFFFFF",
  offWhite:   "#F7F9FC",
  surface:    "#EEF2F8",
  border:     "rgba(10,22,40,0.1)",
  borderLight:"rgba(255,255,255,0.12)",
  text:       "#0A1628",
  textMid:    "#4A6080",
  textLight:  "#8299B4",
  urgent:     "#E53935",
  urgentBg:   "#FFF0F0",
  moderate:   "#F59E0B",
  moderateBg: "#FFFBEB",
  low:        "#10B981",
  lowBg:      "#F0FDF9",
};

// ─── GLOBAL CSS ──────────────────────────────────────────────────
const GLOBAL_CSS = `
@import url('${FONT_LINK}');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
html,body{height:100%;overflow:hidden;}
body{font-family:'DM Sans',sans-serif;background:${C.navy};-webkit-font-smoothing:antialiased;-webkit-tap-highlight-color:transparent;}
input,textarea,button,select{font-family:'DM Sans',sans-serif;}
::-webkit-scrollbar{width:3px;}
::-webkit-scrollbar-track{background:transparent;}
::-webkit-scrollbar-thumb{background:${C.border};border-radius:3px;}

@keyframes fadeUp{from{opacity:0;transform:translateY(18px);}to{opacity:1;transform:translateY(0);}}
@keyframes fadeIn{from{opacity:0;}to{opacity:1;}}
@keyframes slideUp{from{opacity:0;transform:translateY(100%);}to{opacity:1;transform:translateY(0);}}
@keyframes slideRight{from{opacity:0;transform:translateX(-16px);}to{opacity:1;transform:translateX(0);}}
@keyframes pulse{0%,100%{opacity:1;transform:scale(1);}50%{opacity:0.5;transform:scale(1.5);}}
@keyframes spin{to{transform:rotate(360deg);}}
@keyframes ripple{0%{transform:scale(0);opacity:0.4;}100%{transform:scale(4);opacity:0;}}
@keyframes blink{0%,100%{opacity:1;}50%{opacity:0;}}
@keyframes typing{0%,60%,100%{transform:translateY(0);}30%{transform:translateY(-5px);}}

.fu{animation:fadeUp 0.5s ease both;}
.fi{animation:fadeIn 0.35s ease both;}
.su{animation:slideUp 0.4s cubic-bezier(0.16,1,0.3,1) both;}
.sr{animation:slideRight 0.35s ease both;}

.screen{position:absolute;inset:0;display:flex;flex-direction:column;overflow:hidden;}
.scroll{overflow-y:auto;flex:1;}
`;

// ─── URGENCY CONFIG ───────────────────────────────────────────────
const URGENCY = {
  urgent:   { label:"Urgent",   color: C.urgent,   bg: C.urgentBg,   icon:"🔴", action:"Go to ER or call 911 now." },
  moderate: { label:"Moderate", color: C.moderate, bg: C.moderateBg, icon:"🟡", action:"Visit urgent care within 4–6 hours." },
  low:      { label:"Low",      color: C.low,      bg: C.lowBg,      icon:"🟢", action:"Home care — monitor and follow up with your provider." },
};

// ─── SYMPTOM STEPS ───────────────────────────────────────────────
const SYMPTOM_STEPS = [
  { id:"child_name", label:"Child's name & age", type:"text",   hint:"e.g. Maya, 4 years old" },
  { id:"concern",    label:"What's going on?",   type:"textarea",hint:"Describe in plain language — like texting a friend" },
  { id:"duration",   label:"How long has this been happening?", type:"choice", options:["Just started today","1–2 days","3–5 days","More than a week"] },
  { id:"severity",   label:"How severe does it seem?",          type:"choice", options:["Mild — child seems okay","Moderate — clearly uncomfortable","Severe — very distressed","Not sure"] },
  { id:"scale",      label:"How worried are you? (1–10)",       type:"scale" },
  { id:"contact",    label:"Best callback number",              type:"tel",   hint:"(910) 555-0000" },
];

// ─── MOCK CASES ──────────────────────────────────────────────────
const MOCK_CASES = [
  { id:"C-1042", name:"Amara J.", age:4,  phone:"(910) 555-1234", concern:"Fever 102°F × 2 days, pulling at ear", urgency:"moderate", status:"pending",     time:"4 min ago",  scale:7 },
  { id:"C-1041", name:"Jaylen M.",age:13, phone:"(910) 555-3456", concern:"Chest tightness, mild SOB, known asthma", urgency:"urgent",   status:"escalated",  time:"22 min ago", scale:9 },
  { id:"C-1040", name:"Zoe W.",   age:2,  phone:"(910) 555-9012", concern:"Rash on torso, no fever, started this morning", urgency:"low",  status:"complete",   time:"1 hr ago",   scale:4 },
  { id:"C-1039", name:"Marcus T.",age:8,  phone:"(910) 555-5678", concern:"Stomach pain, no vomiting, ate lunch fine", urgency:"low",     status:"in-progress",time:"2 hrs ago",   scale:3 },
  { id:"C-1038", name:"Priya K.", age:6,  phone:"(910) 555-7890", concern:"Vomiting ×3, won't drink fluids, lethargic", urgency:"moderate",status:"complete",  time:"3 hrs ago",   scale:8 },
];

// ─── HELPERS ─────────────────────────────────────────────────────
function classifyUrgency(concern="", scale=5, severity="") {
  const t = concern.toLowerCase();
  if (["can't breathe","not breathing","unconscious","seizure","unresponsive","severe bleeding","blue lips","anaphylaxis"].some(k=>t.includes(k)) || scale>=9 || severity.includes("Severe")) return "urgent";
  if (["fever","ear","vomiting","lethargic","rash","wheezing","dehydrat","won't drink","stomach pain","chest"].some(k=>t.includes(k)) || scale>=6 || severity.includes("Moderate")) return "moderate";
  return "low";
}

function genCaseId() { return "C-" + Math.floor(1000+Math.random()*9000); }
function now() { return new Date().toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"}); }

// ─── API CALL ─────────────────────────────────────────────────────
async function callClaude(system, userMsg, maxTokens=250) {
  const r = await fetch("https://api.anthropic.com/v1/messages",{
    method:"POST",
    headers:{"Content-Type":"application/json"},
    body:JSON.stringify({
      model:"claude-sonnet-4-20250514",
      max_tokens:maxTokens,
      system,
      messages:[{role:"user",content:userMsg}]
    })
  });
  const d = await r.json();
  return d.content?.[0]?.text || "";
}

// ─── SHARED UI COMPONENTS ────────────────────────────────────────

function UrgencyPill({ urgency, small }) {
  const u = URGENCY[urgency]||URGENCY.low;
  return (
    <span style={{
      background: u.bg, color: u.color,
      borderRadius:100, padding: small?"2px 8px":"4px 12px",
      fontSize: small?10:11, fontWeight:700,
      textTransform:"uppercase", letterSpacing:"0.06em",
      border:`1px solid ${u.color}22`
    }}>{u.icon} {u.label}</span>
  );
}

function StatusPill({ status }) {
  const map = {
    pending:      { bg:"#EEF2F8", color:C.textMid,   label:"Pending" },
    "in-progress":{ bg:"#FFFBEB", color:C.moderate,  label:"In Progress" },
    complete:     { bg:"#F0FDF9", color:C.low,        label:"Complete" },
    escalated:    { bg:"#FFF0F0", color:C.urgent,     label:"Escalated" },
  };
  const s = map[status]||map.pending;
  return <span style={{background:s.bg,color:s.color,borderRadius:100,padding:"3px 10px",fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.05em"}}>{s.label}</span>;
}

function Spinner({ size=18, color=C.teal }) {
  return <div style={{width:size,height:size,border:`2px solid ${color}33`,borderTop:`2px solid ${color}`,borderRadius:"50%",animation:"spin 0.7s linear infinite"}} />;
}

function TypingIndicator() {
  return (
    <div style={{display:"flex",gap:4,alignItems:"center",padding:"10px 14px"}}>
      {[0,1,2].map(i=><div key={i} style={{width:6,height:6,borderRadius:"50%",background:C.teal,opacity:0.7,animation:`typing 1.2s ${i*0.18}s infinite`}}/>)}
    </div>
  );
}

function NurseAvatar({ size=32 }) {
  return (
    <div style={{width:size,height:size,borderRadius:"50%",background:`linear-gradient(135deg,${C.teal},${C.navyLight})`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:size*0.45,flexShrink:0}}>
      👩🏾‍⚕️
    </div>
  );
}

function TopBar({ title, subtitle, onBack, light, action }) {
  return (
    <div style={{
      background: light ? C.white : C.navy,
      borderBottom:`1px solid ${light?C.border:C.borderLight}`,
      padding:"12px 16px 10px",
      flexShrink:0,
      display:"flex", alignItems:"center", gap:12
    }}>
      {onBack && (
        <button onClick={onBack} style={{background:"transparent",border:"none",cursor:"pointer",padding:"4px 6px 4px 0",color:light?C.navyLight:C.white,fontSize:20,lineHeight:1,display:"flex",alignItems:"center"}}>
          ‹
        </button>
      )}
      <div style={{flex:1}}>
        <div style={{fontFamily:"'Bitter',serif",fontSize:16,fontWeight:700,color:light?C.navy:C.white,letterSpacing:"-0.01em"}}>{title}</div>
        {subtitle&&<div style={{fontSize:11,color:light?C.textMid:C.textLight,marginTop:1}}>{subtitle}</div>}
      </div>
      {action}
    </div>
  );
}

function Card({ children, style={}, onClick }) {
  return (
    <div onClick={onClick} style={{
      background:C.white,borderRadius:14,
      border:`1px solid ${C.border}`,
      padding:"14px 16px",
      cursor:onClick?"pointer":"default",
      transition:"box-shadow 0.15s,border-color 0.15s",
      ...style
    }}
    onMouseEnter={e=>onClick&&(e.currentTarget.style.borderColor=C.teal)}
    onMouseLeave={e=>onClick&&(e.currentTarget.style.borderColor=C.border)}>
      {children}
    </div>
  );
}

function Btn({ children, onClick, variant="primary", disabled, style={}, small }) {
  const base = {
    border:"none",borderRadius:100,cursor:disabled?"not-allowed":"pointer",
    fontFamily:"'DM Sans',sans-serif",fontWeight:600,
    fontSize:small?13:15,
    padding:small?"8px 16px":"14px 28px",
    transition:"all 0.15s",
    opacity:disabled?0.45:1,
    display:"inline-flex",alignItems:"center",justifyContent:"center",gap:8,
    ...style
  };
  const variants = {
    primary:  { background:C.teal,       color:C.navy,    boxShadow:`0 4px 16px ${C.tealGlow}` },
    dark:     { background:C.navy,       color:C.white },
    outline:  { background:"transparent",color:C.navy,    border:`1.5px solid ${C.border}` },
    outlineW: { background:"transparent",color:C.white,   border:`1.5px solid ${C.borderLight}` },
    danger:   { background:C.urgentBg,   color:C.urgent,  border:`1.5px solid ${C.urgent}33` },
    ghost:    { background:C.tealGlow,   color:C.teal },
  };
  return <button style={{...base,...variants[variant]}} onClick={disabled?undefined:onClick}>{children}</button>;
}

// ─── SCREEN 1: SPLASH ────────────────────────────────────────────
function SplashScreen({ onRole }) {
  return (
    <div className="screen" style={{background:C.navy,justifyContent:"center",alignItems:"center",padding:32}}>
      <style>{GLOBAL_CSS}</style>

      <div style={{position:"absolute",top:"15%",left:"50%",transform:"translateX(-50%)",width:260,height:260,borderRadius:"50%",background:C.tealGlow,filter:"blur(60px)",pointerEvents:"none"}}/>

      <div className="fu" style={{textAlign:"center",marginBottom:48}}>
        <div style={{display:"inline-flex",alignItems:"center",gap:8,background:"rgba(0,201,167,0.1)",border:`1px solid ${C.teal}33`,borderRadius:100,padding:"6px 16px",marginBottom:24}}>
          <span style={{width:6,height:6,borderRadius:"50%",background:C.teal,animation:"pulse 2s infinite",display:"inline-block"}}/>
          <span style={{fontSize:11,fontWeight:700,color:C.teal,letterSpacing:"0.08em"}}>NURSES AVAILABLE NOW</span>
        </div>
        <div style={{fontFamily:"'Bitter',serif",fontSize:38,fontWeight:900,color:C.white,lineHeight:1.1,letterSpacing:"-0.03em",marginBottom:12}}>
          Ask A<br/><span style={{color:C.teal}}>Nurse</span>
        </div>
        <div style={{fontSize:14,color:C.textLight,lineHeight:1.7,maxWidth:280,margin:"0 auto"}}>
          Expert pediatric nurse guidance for every family — real answers, not a Google rabbit hole.
        </div>
      </div>

      <div className="fu" style={{width:"100%",maxWidth:320,display:"flex",flexDirection:"column",gap:12,animationDelay:"0.1s"}}>
        <button onClick={()=>onRole("parent")} style={{
          background:C.teal,color:C.navy,border:"none",borderRadius:14,
          padding:"18px 24px",display:"flex",alignItems:"center",gap:16,
          cursor:"pointer",textAlign:"left",width:"100%",
          boxShadow:`0 8px 32px ${C.tealGlow}`,transition:"transform 0.15s"
        }} onMouseEnter={e=>e.currentTarget.style.transform="translateY(-2px)"}
           onMouseLeave={e=>e.currentTarget.style.transform="translateY(0)"}>
          <span style={{fontSize:28}}>👨‍👩‍👧</span>
          <div>
            <div style={{fontFamily:"'Bitter',serif",fontSize:17,fontWeight:700}}>I'm a Parent</div>
            <div style={{fontSize:12,opacity:0.75,marginTop:2}}>Get guidance for my child</div>
          </div>
          <span style={{marginLeft:"auto",fontSize:20,opacity:0.6}}>›</span>
        </button>

        <button onClick={()=>onRole("nurse")} style={{
          background:C.navyMid,color:C.white,border:`1px solid ${C.borderLight}`,borderRadius:14,
          padding:"18px 24px",display:"flex",alignItems:"center",gap:16,
          cursor:"pointer",textAlign:"left",width:"100%",transition:"background 0.15s"
        }} onMouseEnter={e=>e.currentTarget.style.background=C.navyLight}
           onMouseLeave={e=>e.currentTarget.style.background=C.navyMid}>
          <span style={{fontSize:28}}>👩🏾‍⚕️</span>
          <div>
            <div style={{fontFamily:"'Bitter',serif",fontSize:17,fontWeight:700}}>I'm a Nurse</div>
            <div style={{fontSize:12,opacity:0.6,marginTop:2}}>Access nurse dashboard</div>
          </div>
          <span style={{marginLeft:"auto",fontSize:20,opacity:0.4}}>›</span>
        </button>
      </div>

      <div className="fu" style={{position:"absolute",bottom:28,textAlign:"center",animationDelay:"0.2s"}}>
        <div style={{fontSize:10,color:C.textLight,lineHeight:1.7,maxWidth:300}}>
          ⚠️ Not a medical emergency line. For life-threatening emergencies, call 911.
        </div>
      </div>
    </div>
  );
}

// ─── SCREEN 2: PARENT HOME ───────────────────────────────────────
function HomeScreen({ onNav, membership, onUpgrade }) {
  const [notifEnabled, setNotifEnabled] = useState(false);

  function requestNotif() {
    if ("Notification" in window) {
      Notification.requestPermission().then(p => setNotifEnabled(p==="granted"));
    }
    setNotifEnabled(true);
  }

  return (
    <div className="screen" style={{background:C.offWhite}}>
      {/* HERO HEADER */}
      <div style={{background:C.navy,padding:"44px 20px 28px",flexShrink:0,position:"relative",overflow:"hidden"}}>
        <div style={{position:"absolute",top:-30,right:-30,width:140,height:140,borderRadius:"50%",background:C.tealGlow}}/>
        <div style={{position:"absolute",bottom:-20,left:-20,width:100,height:100,borderRadius:"50%",background:"rgba(255,255,255,0.03)"}}/>
        <div className="fu" style={{position:"relative"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16}}>
            <div>
              <div style={{fontFamily:"'Bitter',serif",fontSize:26,fontWeight:900,color:C.white,letterSpacing:"-0.02em",lineHeight:1.15}}>Ask A<br/><span style={{color:C.teal}}>Nurse</span></div>
            </div>
            <div style={{textAlign:"right"}}>
              {membership ? (
                <div style={{background:C.tealGlow,border:`1px solid ${C.teal}44`,borderRadius:100,padding:"4px 12px",display:"inline-flex",alignItems:"center",gap:6}}>
                  <span style={{fontSize:10,fontWeight:700,color:C.teal}}>✦ MEMBER</span>
                </div>
              ) : (
                <Btn small variant="ghost" onClick={onUpgrade}>Join Free →</Btn>
              )}
            </div>
          </div>
          <div style={{fontSize:13,color:C.textLight,lineHeight:1.6}}>
            Expert pediatric guidance. Real nurses. Right now.
          </div>
        </div>
      </div>

      <div className="scroll" style={{padding:"16px 16px 24px",display:"flex",flexDirection:"column",gap:12}}>

        {/* PRIMARY CTA */}
        <div className="fu" style={{animationDelay:"0.05s"}}>
          <button onClick={()=>onNav("checker")} style={{
            width:"100%",background:C.teal,color:C.navy,border:"none",borderRadius:16,
            padding:"20px",display:"flex",alignItems:"center",gap:16,cursor:"pointer",
            boxShadow:`0 8px 28px ${C.tealGlow}`,textAlign:"left"
          }}>
            <div style={{width:52,height:52,background:"rgba(10,22,40,0.12)",borderRadius:14,display:"flex",alignItems:"center",justifyContent:"center",fontSize:26,flexShrink:0}}>🩺</div>
            <div>
              <div style={{fontFamily:"'Bitter',serif",fontSize:18,fontWeight:700,letterSpacing:"-0.01em"}}>Start Symptom Check</div>
              <div style={{fontSize:12,opacity:0.75,marginTop:2}}>AI-guided triage · Get urgency score</div>
            </div>
            <div style={{marginLeft:"auto",fontSize:22,opacity:0.6}}>›</div>
          </button>
        </div>

        {/* CALL + TEXT ROW */}
        <div className="fu" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,animationDelay:"0.1s"}}>
          <a href="tel:+8009222201" style={{
            background:C.navy,color:C.white,borderRadius:14,padding:"16px 14px",
            display:"flex",alignItems:"center",gap:10,textDecoration:"none",
            border:`1px solid ${C.borderLight}`
          }}>
            <span style={{fontSize:22}}>📞</span>
            <div>
              <div style={{fontFamily:"'Bitter',serif",fontSize:14,fontWeight:700}}>Call a Nurse</div>
              <div style={{fontSize:10,opacity:0.55,marginTop:1}}>Live · Free</div>
            </div>
          </a>
          <a href="sms:+8009222201?body=NURSE" style={{
            background:C.white,color:C.navy,borderRadius:14,padding:"16px 14px",
            display:"flex",alignItems:"center",gap:10,textDecoration:"none",
            border:`1px solid ${C.border}`
          }}>
            <span style={{fontSize:22}}>💬</span>
            <div>
              <div style={{fontFamily:"'Bitter',serif",fontSize:14,fontWeight:700}}>Text a Nurse</div>
              <div style={{fontSize:10,color:C.textMid,marginTop:1}}>Text NURSE</div>
            </div>
          </a>
        </div>

        {/* NOTIF PROMPT */}
        {!notifEnabled && (
          <div className="fu" style={{animationDelay:"0.15s"}}>
            <Card>
              <div style={{display:"flex",alignItems:"center",gap:12}}>
                <div style={{fontSize:24}}>🔔</div>
                <div style={{flex:1}}>
                  <div style={{fontSize:14,fontWeight:600,color:C.navy}}>Enable push notifications</div>
                  <div style={{fontSize:12,color:C.textMid,marginTop:2}}>Get nurse follow-ups directly</div>
                </div>
                <Btn small variant="ghost" onClick={requestNotif}>Enable</Btn>
              </div>
            </Card>
          </div>
        )}

        {/* TRUST GRID */}
        <div className="fu" style={{animationDelay:"0.2s"}}>
          <div style={{fontSize:11,fontWeight:700,color:C.textLight,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:10}}>Why families trust us</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
            {[
              ["👩🏾‍⚕️","Pediatric RNs & NPs","20+ years experience"],
              ["🛡️","Trauma-informed","Safe, no-judgment care"],
              ["⚡","Rapid response","Under 30 min avg"],
              ["🏅","All families","Every background welcome"],
            ].map(([icon,title,sub])=>(
              <Card key={title} style={{padding:"12px 14px"}}>
                <div style={{fontSize:22,marginBottom:6}}>{icon}</div>
                <div style={{fontSize:13,fontWeight:600,color:C.navy}}>{title}</div>
                <div style={{fontSize:11,color:C.textMid,marginTop:2}}>{sub}</div>
              </Card>
            ))}
          </div>
        </div>

        {/* MEMBERSHIP CARD */}
        {!membership && (
          <div className="fu" style={{animationDelay:"0.25s"}}>
            <div style={{background:C.navy,borderRadius:16,padding:"20px",position:"relative",overflow:"hidden"}}>
              <div style={{position:"absolute",top:-20,right:-20,width:100,height:100,borderRadius:"50%",background:C.tealGlow}}/>
              <div style={{fontSize:12,color:C.teal,fontWeight:700,marginBottom:6,letterSpacing:"0.05em"}}>✦ UNLIMITED ACCESS</div>
              <div style={{fontFamily:"'Bitter',serif",fontSize:20,fontWeight:700,color:C.white,marginBottom:6}}>$29 / month</div>
              <div style={{fontSize:13,color:C.textLight,marginBottom:16,lineHeight:1.6}}>Unlimited nurse calls · Priority routing · 24-hr follow-up texts · Mental health navigation</div>
              <Btn onClick={onUpgrade} style={{width:"100%"}}>Join Now →</Btn>
            </div>
          </div>
        )}

        {/* DISCLAIMER */}
        <div style={{background:C.surface,borderRadius:10,padding:"10px 12px",marginTop:4}}>
          <div style={{fontSize:10,color:C.textLight,lineHeight:1.65}}>
            ⚠️ <strong style={{color:C.textMid}}>Not medical advice.</strong> Our nurses provide guidance only — not diagnosis or treatment. Always call 911 for emergencies.
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── SCREEN 3: SYMPTOM CHECKER ───────────────────────────────────
function SymptomChecker({ onComplete, onBack }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [scaleVal, setScaleVal] = useState(5);
  const [textVal, setTextVal] = useState("");

  const current = SYMPTOM_STEPS[step];
  const progress = (step / SYMPTOM_STEPS.length) * 100;

  function advance(value) {
    const next = { ...answers, [current.id]: value };
    setAnswers(next);
    setTextVal("");
    if (step + 1 < SYMPTOM_STEPS.length) {
      setStep(s => s + 1);
    } else {
      const urgency = classifyUrgency(next.concern||"", parseInt(next.scale||5), next.severity||"");
      onComplete({ ...next, urgency, caseId: genCaseId(), timestamp: now() });
    }
  }

  const scaleColors = ["","","#10B981","#10B981","#10B981","#F59E0B","#F59E0B","#F59E0B","#E53935","#E53935","#E53935"];

  return (
    <div className="screen" style={{background:C.offWhite}}>
      <TopBar title="Symptom Check" subtitle={`Step ${step+1} of ${SYMPTOM_STEPS.length}`} onBack={step>0?()=>setStep(s=>s-1):onBack} light />

      {/* PROGRESS */}
      <div style={{height:3,background:C.surface,flexShrink:0}}>
        <div style={{height:"100%",background:C.teal,width:`${progress}%`,transition:"width 0.4s ease",borderRadius:"0 3px 3px 0"}}/>
      </div>

      <div className="scroll" style={{padding:"24px 20px"}}>
        <div key={step} className="sr">
          <div style={{fontSize:11,fontWeight:700,color:C.teal,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>
            {step+1} of {SYMPTOM_STEPS.length}
          </div>
          <div style={{fontFamily:"'Bitter',serif",fontSize:22,fontWeight:700,color:C.navy,lineHeight:1.25,marginBottom:8}}>
            {current.label}
          </div>

          {/* TEXT / TEXTAREA / TEL */}
          {(current.type==="text"||current.type==="textarea"||current.type==="tel") && (
            <div style={{marginTop:20}}>
              {current.type==="textarea" ? (
                <textarea
                  value={textVal}
                  onChange={e=>setTextVal(e.target.value)}
                  placeholder={current.hint}
                  rows={4}
                  style={{width:"100%",border:`1.5px solid ${C.border}`,borderRadius:12,padding:"14px 16px",fontFamily:"'DM Sans',sans-serif",fontSize:15,color:C.navy,background:C.white,outline:"none",resize:"none",lineHeight:1.6}}
                />
              ) : (
                <input
                  type={current.type}
                  value={textVal}
                  onChange={e=>setTextVal(e.target.value)}
                  placeholder={current.hint}
                  style={{width:"100%",border:`1.5px solid ${C.border}`,borderRadius:12,padding:"14px 16px",fontFamily:"'DM Sans',sans-serif",fontSize:15,color:C.navy,background:C.white,outline:"none"}}
                />
              )}
              <Btn onClick={()=>textVal.trim()&&advance(textVal.trim())} disabled={!textVal.trim()} style={{width:"100%",marginTop:12}}>
                Continue →
              </Btn>
            </div>
          )}

          {/* CHOICE */}
          {current.type==="choice" && (
            <div style={{display:"flex",flexDirection:"column",gap:10,marginTop:20}}>
              {current.options.map(opt=>(
                <button key={opt} onClick={()=>advance(opt)} style={{
                  background:C.white,border:`1.5px solid ${C.border}`,borderRadius:12,
                  padding:"14px 18px",textAlign:"left",cursor:"pointer",
                  fontFamily:"'DM Sans',sans-serif",fontSize:14,color:C.navy,fontWeight:500,
                  display:"flex",alignItems:"center",justifyContent:"space-between",
                  transition:"border-color 0.15s,background 0.15s"
                }}
                onMouseEnter={e=>{e.currentTarget.style.borderColor=C.teal;e.currentTarget.style.background="#F0FDFB";}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor=C.border;e.currentTarget.style.background=C.white;}}>
                  {opt} <span style={{color:C.teal,fontSize:18}}>›</span>
                </button>
              ))}
            </div>
          )}

          {/* SCALE */}
          {current.type==="scale" && (
            <div style={{marginTop:24}}>
              <div style={{textAlign:"center",marginBottom:16}}>
                <span style={{fontFamily:"'Bitter',serif",fontSize:56,fontWeight:900,color:scaleColors[scaleVal]||C.teal}}>{scaleVal}</span>
                <div style={{fontSize:12,color:C.textMid,marginTop:4}}>
                  {scaleVal<=3?"Calm — not too worried":scaleVal<=6?"Somewhat concerned":scaleVal<=8?"Very worried":"About to go to the ER"}
                </div>
              </div>
              <input type="range" min={1} max={10} step={1} value={scaleVal}
                onChange={e=>setScaleVal(parseInt(e.target.value))}
                style={{width:"100%",accentColor:scaleColors[scaleVal]||C.teal,cursor:"pointer"}}
              />
              <div style={{display:"flex",justifyContent:"space-between",fontSize:10,color:C.textLight,marginTop:4}}>
                <span>1 — Calm</span><span>10 — Emergency</span>
              </div>
              <Btn onClick={()=>advance(String(scaleVal))} style={{width:"100%",marginTop:20}}>
                Continue →
              </Btn>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── SCREEN 4: TRIAGE CHAT ───────────────────────────────────────
function TriageChat({ intake, onComplete, onBack }) {
  const [messages, setMessages] = useState([
    { role:"nurse", text:`Hi! I have your symptom check. Let me review everything for ${intake.child_name||"your child"} and generate a full guidance summary. Give me just a moment...`, ts: now() }
  ]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [result, setResult] = useState(null);
  const [input, setInput] = useState("");
  const [chatHistory, setChatHistory] = useState([]);
  const bottomRef = useRef();

  useEffect(()=>{ bottomRef.current?.scrollIntoView({behavior:"smooth"}); },[messages,loading]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(()=>{ runInitialAnalysis(); },[]);

  async function runInitialAnalysis() {
    setLoading(true);
    try {
      const raw = await callClaude(
        `You are a highly experienced pediatric nurse with 20+ years of clinical experience in pediatric emergency, urgent care, and primary care. You work for Ask A Nurse, a pediatric nurse guidance line.

Your job is to provide specific, clinically-informed, parent-friendly triage guidance. You are warm, direct, and thorough. You speak like a trusted nurse friend — not a liability-scared chatbot.

CRITICAL RULES:
- Never say "consult a healthcare provider" as your ONLY answer — that is useless to a scared parent
- Always address the SPECIFIC symptoms mentioned — never be generic
- Always give specific home care instructions (fever reducer dosing by weight if relevant, hydration guidance, positioning, comfort measures)
- Always give specific warning signs based on the ACTUAL complaint
- Always give a clear time-based recommendation (go NOW, within 2 hours, within 24 hours, monitor at home)
- Be warm and reassuring but honest — if something sounds serious, say so clearly
- Reference the child by name and their specific age in your response
- Never diagnose — but DO give clinically specific guidance

Respond ONLY with a valid JSON object — no markdown, no preamble:
{
  "summary": "2-3 sentences describing exactly what is going on with this specific child — reference their name, age, exact symptoms, and duration. Be specific not generic.",
  "recommendation": "A clear time-based action with clinical reasoning. Example: 'Take Maya to urgent care within the next 2-3 hours. At 4 years old with a 2-day fever and ear pulling, this pattern is classic for otitis media and she likely needs to be evaluated today and may need antibiotics.' Be exactly this specific.",
  "watchFor": "List 3-4 specific warning signs for THIS child's exact complaint that would mean go to the ER immediately. Be specific to their age and symptoms.",
  "homecare": "2-3 specific things the parent can do RIGHT NOW while deciding — include age-appropriate dosing if relevant, comfort measures, hydration, positioning etc.",
  "nurseMessage": "2-3 warm sentences speaking directly to THIS parent — use the child's name, acknowledge their specific worry, validate them, and give them confidence. Sound like a real experienced nurse, not a robot.",
  "followUpSMS": "A warm specific 24-hr follow-up SMS under 160 chars referencing the child by name and their specific concern. Include 💚"
}`,
        `Child name: ${intake.child_name}.
Concern: ${intake.concern}.
Duration: ${intake.duration}.
Severity: ${intake.severity}.
Parent worry scale: ${intake.scale}/10.
Urgency classification: ${intake.urgency}.
Contact: ${intake.contact}.

Give a thorough, clinically specific, parent-friendly triage response. Reference the child by name. Be specific to their age and exact symptoms. Do not be generic.`,
        800
      );
      const clean = raw.replace(/```json|```/g,"").trim();
      const parsed = JSON.parse(clean);
      const full = { ...intake, ...parsed };
      setMessages(m=>[...m,
        { role:"nurse", text: parsed.nurseMessage, ts: now() },
        { role:"system", result: full }
      ]);
      setDone(true);
    } catch(e) {
      setMessages(m=>[...m,{ role:"nurse", text:"I've reviewed your concern. Please call our nurse line directly at (800) 922-2201 for personalized guidance.", ts:now() }]);
      setDone(true);
    }
    setLoading(false);
  }

  async function sendMessage() {
    if (!input.trim()||loading) return;
    const userMsg = input.trim();
    setInput("");
    const newHistory = [...chatHistory, { role:"user", content:userMsg }];
    setChatHistory(newHistory);
    setMessages(m=>[...m,{ role:"user", text:userMsg, ts:now() }]);
    setLoading(true);
    try {
      const reply = await callClaude(
        `You are a highly experienced pediatric nurse on the Ask A Nurse line with 20+ years of experience in pediatric emergency and primary care.
Answer follow-up questions like a trusted nurse friend — specific, warm, direct, and clinically informed.
Never give generic answers. Always address the exact question asked with clinical specificity.
Reference the child by name and their specific symptoms in your answer.
Give real actionable guidance — not just "see a doctor."
If asked about medications give age-appropriate dosing guidance.
If asked about symptoms explain exactly what they mean in plain language a parent can understand.
Keep responses to 3-4 sentences — clear, direct, and specific.
Never diagnose. Never prescribe controlled substances. If something is outside your scope say so warmly and explain why.
Child context: ${intake.child_name||"the child"}, concern: ${intake.concern||""}, urgency level: ${intake.urgency}.`,
        `${newHistory.map(h=>`${h.role}: ${h.content}`).join("\n")}\nuser: ${userMsg}`,
        200
      );
      const newHistoryWithAssist = [...newHistory, { role:"assistant", content:reply }];
      setChatHistory(newHistoryWithAssist);
      setMessages(m=>[...m,{ role:"nurse", text:reply, ts:now() }]);
    } catch {
      setMessages(m=>[...m,{ role:"nurse", text:"I'm having trouble connecting. Please call the nurse line directly.", ts:now() }]);
    }
    setLoading(false);
  }

  const u = URGENCY[intake.urgency]||URGENCY.low;

  return (
    <div className="screen" style={{background:C.offWhite}}>
      <div style={{background:C.navy,padding:"14px 16px 12px",flexShrink:0,display:"flex",alignItems:"center",gap:12}}>
        <button onClick={onBack} style={{background:"transparent",border:"none",cursor:"pointer",color:C.white,fontSize:22,padding:"2px 6px 2px 0"}}>‹</button>
        <NurseAvatar size={36}/>
        <div style={{flex:1}}>
          <div style={{fontFamily:"'Bitter',serif",fontSize:15,fontWeight:700,color:C.white}}>Ask A Nurse</div>
          <div style={{fontSize:11,color:C.teal,display:"flex",alignItems:"center",gap:4}}>
            <span style={{width:5,height:5,borderRadius:"50%",background:C.teal,display:"inline-block",animation:"pulse 2s infinite"}}/>
            Online · Nurse guidance only · Not medical advice
          </div>
        </div>
        {done && <UrgencyPill urgency={intake.urgency} small />}
      </div>

      <div className="scroll" style={{padding:"16px 14px",display:"flex",flexDirection:"column",gap:10}}>
        {messages.map((msg,i)=>(
          <div key={i} className="fi" style={{animationDelay:`${i*0.05}s`}}>
            {msg.role==="user" && (
              <div style={{display:"flex",justifyContent:"flex-end"}}>
                <div style={{background:C.navy,color:C.white,borderRadius:"16px 16px 3px 16px",padding:"10px 14px",maxWidth:"78%",fontSize:14,lineHeight:1.6}}>
                  {msg.text}
                  <div style={{fontSize:10,opacity:0.5,marginTop:4,textAlign:"right"}}>{msg.ts}</div>
                </div>
              </div>
            )}
            {msg.role==="nurse" && (
              <div style={{display:"flex",gap:8,alignItems:"flex-start"}}>
                <NurseAvatar size={28}/>
                <div style={{background:C.white,border:`1px solid ${C.border}`,borderRadius:"16px 16px 16px 3px",padding:"10px 14px",maxWidth:"78%",fontSize:14,lineHeight:1.6,color:C.navy,boxShadow:"0 1px 4px rgba(10,22,40,0.06)"}}>
                  {msg.text}
                  <div style={{fontSize:10,color:C.textLight,marginTop:4}}>{msg.ts}</div>
                </div>
              </div>
            )}
            {msg.role==="system" && msg.result && (
              <div className="fu" style={{marginTop:8}}>
                <div style={{background:u.bg,border:`1.5px solid ${u.color}33`,borderRadius:16,overflow:"hidden"}}>
                  <div style={{background:u.color,padding:"10px 14px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <div style={{fontFamily:"'Bitter',serif",fontSize:14,fontWeight:700,color:"white"}}>Triage Summary · {msg.result.caseId}</div>
                    <span style={{fontSize:11,fontWeight:700,color:"white",opacity:0.9}}>{msg.result.timestamp}</span>
                  </div>
                  <div style={{padding:14,display:"flex",flexDirection:"column",gap:10}}>
                    <div>
                      <div style={{fontSize:10,fontWeight:700,color:C.textLight,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3}}>Summary</div>
                      <div style={{fontSize:13,color:C.navy,lineHeight:1.65}}>{msg.result.summary}</div>
                    </div>
                    <div style={{background:u.color+"22",borderRadius:8,padding:"10px 12px"}}>
                      <div style={{fontSize:10,fontWeight:700,color:u.color,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3}}>Recommended Action</div>
                      <div style={{fontSize:13,fontWeight:600,color:C.navy}}>{msg.result.recommendation}</div>
                    </div>
                    <div>
                      <div style={{fontSize:10,fontWeight:700,color:C.textLight,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3}}>Watch For</div>
                      <div style={{fontSize:12,color:C.textMid,lineHeight:1.6}}>{msg.result.watchFor}</div>
                    </div>
                    {msg.result.homecare && (
                      <div style={{background:"#F0FDF9",borderRadius:8,padding:"10px 12px",border:`1px solid ${C.low}33`}}>
                        <div style={{fontSize:10,fontWeight:700,color:C.low,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:3}}>What To Do Right Now</div>
                        <div style={{fontSize:12,color:C.navy,lineHeight:1.65}}>{msg.result.homecare}</div>
                      </div>
                    )}
                    <div style={{fontSize:10,color:C.textLight,lineHeight:1.5,paddingTop:8,borderTop:`1px solid ${u.color}22`}}>
                      ⚠️ Nurse guidance only — not a diagnosis or medical advice. Consult a licensed provider for treatment decisions.
                    </div>
                    <Btn onClick={()=>onComplete(msg.result)} style={{width:"100%"}}>
                      View Full Summary →
                    </Btn>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
        {loading && (
          <div style={{display:"flex",gap:8,alignItems:"flex-start"}}>
            <NurseAvatar size={28}/>
            <div style={{background:C.white,border:`1px solid ${C.border}`,borderRadius:"16px 16px 16px 3px",boxShadow:"0 1px 4px rgba(10,22,40,0.06)"}}>
              <TypingIndicator/>
            </div>
          </div>
        )}
        <div ref={bottomRef}/>
      </div>

      {/* INPUT */}
      {done && (
        <div style={{padding:"10px 14px 20px",background:C.white,borderTop:`1px solid ${C.border}`,flexShrink:0}}>
          <div style={{fontSize:11,color:C.textLight,marginBottom:6}}>💬 Have a follow-up question? Ask below.</div>
          <div style={{display:"flex",gap:8}}>
            <input value={input} onChange={e=>setInput(e.target.value)}
              onKeyDown={e=>e.key==="Enter"&&!e.shiftKey&&sendMessage()}
              placeholder="Ask a follow-up..."
              style={{flex:1,border:`1.5px solid ${C.border}`,borderRadius:100,padding:"10px 16px",fontFamily:"'DM Sans',sans-serif",fontSize:14,color:C.navy,background:C.offWhite,outline:"none"}}/>
            <button onClick={sendMessage} disabled={!input.trim()||loading} style={{
              width:42,height:42,borderRadius:"50%",background:C.teal,border:"none",
              color:C.navy,fontSize:18,cursor:"pointer",flexShrink:0,
              opacity:(!input.trim()||loading)?0.35:1,transition:"opacity 0.15s",
              display:"flex",alignItems:"center",justifyContent:"center"
            }}>↑</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── SCREEN 5: CONFIRMATION ──────────────────────────────────────
function ConfirmationScreen({ result, onHome, onBack }) {
  const u = URGENCY[result.urgency]||URGENCY.low;
  return (
    <div className="screen" style={{background:C.offWhite}}>
      <TopBar title="Case Summary" subtitle={result.caseId} onBack={onBack} light />
      <div className="scroll" style={{padding:"20px 16px 32px",display:"flex",flexDirection:"column",gap:14}}>

        {/* STATUS BANNER */}
        <div className="fu" style={{background:u.color,borderRadius:16,padding:"20px",textAlign:"center"}}>
          <div style={{fontSize:42,marginBottom:8}}>{u.icon}</div>
          <div style={{fontFamily:"'Bitter',serif",fontSize:22,fontWeight:900,color:"white",marginBottom:4}}>
            {u.label} Priority
          </div>
          <div style={{fontSize:13,color:"rgba(255,255,255,0.85)",lineHeight:1.6}}>{u.action}</div>
        </div>

        {/* SUMMARY CARD */}
        <Card className="fu" style={{animationDelay:"0.1s"}}>
          <div style={{fontSize:11,fontWeight:700,color:C.textLight,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:10}}>Summary</div>
          <div style={{fontSize:14,color:C.navy,lineHeight:1.7,marginBottom:12}}>{result.summary||"Concern captured and under nurse review."}</div>
          <div style={{borderTop:`1px solid ${C.border}`,paddingTop:12}}>
            <div style={{fontSize:11,fontWeight:700,color:C.textLight,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:4}}>Recommended Action</div>
            <div style={{fontSize:14,fontWeight:600,color:C.navy}}>{result.recommendation||u.action}</div>
          </div>
          {result.watchFor && (
            <div style={{borderTop:`1px solid ${C.border}`,paddingTop:12,marginTop:12}}>
              <div style={{fontSize:11,fontWeight:700,color:C.textLight,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:4}}>Watch For</div>
              <div style={{fontSize:13,color:C.textMid,lineHeight:1.6}}>{result.watchFor}</div>
            </div>
          )}
        </Card>

        {/* FOLLOW-UP */}
        {result.followUpSMS && (
          <Card className="fu" style={{background:"#F0FDF9",border:`1px solid ${C.low}33`,animationDelay:"0.15s"}}>
            <div style={{display:"flex",alignItems:"flex-start",gap:10}}>
              <span style={{fontSize:22}}>💬</span>
              <div>
                <div style={{fontSize:12,fontWeight:700,color:C.low,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:4}}>24-Hr Follow-Up Text</div>
                <div style={{fontSize:13,color:C.navy,fontStyle:"italic",lineHeight:1.6}}>"{result.followUpSMS}"</div>
                {result.contact && <div style={{fontSize:11,color:C.textMid,marginTop:4}}>Sending to: {result.contact}</div>}
              </div>
            </div>
          </Card>
        )}

        {/* DISCLAIMER */}
        <div style={{background:C.surface,borderRadius:12,padding:"12px 14px"}}>
          <div style={{fontSize:10,color:C.textMid,lineHeight:1.65}}>⚠️ This is nurse guidance only — not medical advice, diagnosis, or treatment. Always consult a licensed healthcare provider. In emergencies, call 911. Case ID: {result.caseId} · {result.timestamp}</div>
        </div>

        <Btn onClick={onHome} variant="dark" style={{width:"100%"}}>📞 Call a Nurse Now</Btn>
        <Btn onClick={onHome} variant="outline" style={{width:"100%"}}>← Start New Check</Btn>
      </div>
    </div>
  );
}

// ─── SCREEN 6: MEMBERSHIP MODAL ──────────────────────────────────
function MembershipModal({ onClose, onJoin }) {
  const plans = [
    { id:"free",   name:"Community",    price:"Free",  sub:"For qualifying families", features:["1 nurse call/month","SMS text guidance","ER navigation","Resource follow-up"], cta:"Get Started" },
    { id:"member", name:"Family",       price:"$29",   per:"/mo", sub:"Most popular", features:["Unlimited nurse calls","Priority routing","24-hr follow-up texts","Mental health navigation","Medication education"], cta:"Join Now", featured:true },
    { id:"partner",name:"Community Partner", price:"$199", per:"/mo", sub:"Orgs & schools", features:["50 families covered","Dedicated account manager","Monthly utilization report","Custom intake","Quarterly staff training"], cta:"Contact Us" },
  ];
  return (
    <div className="su" style={{position:"fixed",inset:0,background:"rgba(10,22,40,0.75)",zIndex:200,display:"flex",alignItems:"flex-end",justifyContent:"center"}}>
      <div style={{background:C.white,borderRadius:"20px 20px 0 0",width:"100%",maxWidth:500,padding:"20px 20px 36px",maxHeight:"88vh",overflowY:"auto"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
          <div style={{fontFamily:"'Bitter',serif",fontSize:20,fontWeight:700,color:C.navy}}>Choose a Plan</div>
          <button onClick={onClose} style={{background:C.surface,border:"none",borderRadius:"50%",width:32,height:32,cursor:"pointer",fontSize:16,color:C.textMid}}>✕</button>
        </div>
        {plans.map(p=>(
          <div key={p.id} style={{
            border:`${p.featured?"2px":"1px"} solid ${p.featured?C.teal:C.border}`,
            borderRadius:14,padding:"16px",marginBottom:10,position:"relative",
            background: p.featured ? "#F0FDFB" : C.white
          }}>
            {p.featured && <div style={{position:"absolute",top:-12,left:"50%",transform:"translateX(-50%)",background:C.teal,color:C.navy,fontSize:10,fontWeight:700,padding:"3px 12px",borderRadius:100,textTransform:"uppercase",letterSpacing:"0.06em",whiteSpace:"nowrap"}}>Most Popular</div>}
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
              <div>
                <div style={{fontFamily:"'Bitter',serif",fontSize:16,fontWeight:700,color:C.navy}}>{p.name}</div>
                <div style={{fontSize:11,color:C.textMid}}>{p.sub}</div>
              </div>
              <div style={{textAlign:"right"}}>
                <span style={{fontFamily:"'Bitter',serif",fontSize:22,fontWeight:900,color:C.navy}}>{p.price}</span>
                {p.per&&<span style={{fontSize:12,color:C.textMid}}>{p.per}</span>}
              </div>
            </div>
            <ul style={{listStyle:"none",marginBottom:12}}>
              {p.features.map(f=><li key={f} style={{fontSize:12,color:C.textMid,padding:"3px 0",display:"flex",gap:6}}>
                <span style={{color:C.teal,fontWeight:700}}>✓</span> {f}
              </li>)}
            </ul>
            <Btn variant={p.featured?"primary":"outline"} small style={{width:"100%"}} onClick={()=>onJoin(p.id)}>
              {p.cta}
            </Btn>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── SCREEN 7: NURSE DASHBOARD ───────────────────────────────────
function NurseDashboard({ onCase, onLogout }) {
  const [cases, setCases] = useState(MOCK_CASES);
  const [filter, setFilter] = useState("all");
  const [tab, setTab] = useState("queue");

  useEffect(() => {
    try {
      const q = query(collection(db, "cases"), orderBy("createdAt", "desc"));
      const unsub = onSnapshot(q, (snapshot) => {
        const live = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        if (live.length > 0) setCases(live);
      });
      return () => unsub();
    } catch(e) {
      console.error("Firebase error:", e);
    }
  }, []);

  const filtered = filter==="all" ? cases : cases.filter(c=>c.urgency===filter||c.status===filter);
  const stats = {
    total: cases.length,
    urgent: cases.filter(c=>c.urgency==="urgent").length,
    pending: cases.filter(c=>c.status==="pending").length,
    done: cases.filter(c=>c.status==="complete").length,
  };

  const urgencyOrder = { urgent:0, moderate:1, low:2 };
  const sorted = [...filtered].sort((a,b)=>(urgencyOrder[a.urgency]||2)-(urgencyOrder[b.urgency]||2));

  return (
    <div className="screen" style={{background:C.offWhite}}>
      <div style={{background:C.navy,padding:"44px 16px 0",flexShrink:0}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
          <div>
            <div style={{fontFamily:"'Bitter',serif",fontSize:22,fontWeight:900,color:C.white,letterSpacing:"-0.02em"}}>Nurse Dashboard</div>
            <div style={{fontSize:11,color:C.textLight,marginTop:1}}>Ask A Nurse · Operations</div>
          </div>
          <button onClick={onLogout} style={{background:C.navyMid,border:`1px solid ${C.borderLight}`,borderRadius:100,padding:"6px 12px",color:C.textLight,fontSize:11,fontWeight:600,cursor:"pointer"}}>
            Log out
          </button>
        </div>
        {/* STAT ROW */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8,marginBottom:16}}>
          {[["Total",stats.total,C.white],["Urgent",stats.urgent,C.urgent],["Pending",stats.pending,C.moderate],["Done",stats.done,C.low]].map(([l,v,c])=>(
            <div key={l} style={{background:"rgba(255,255,255,0.06)",borderRadius:10,padding:"10px 8px",textAlign:"center"}}>
              <div style={{fontFamily:"'Bitter',serif",fontSize:24,fontWeight:900,color:c}}>{v}</div>
              <div style={{fontSize:10,color:C.textLight,marginTop:1}}>{l}</div>
            </div>
          ))}
        </div>
        {/* TABS */}
        <div style={{display:"flex",borderBottom:`1px solid ${C.borderLight}`}}>
          {[["queue","Case Queue"],["insights","Insights"]].map(([key,label])=>(
            <button key={key} onClick={()=>setTab(key)} style={{
              flex:1,padding:"10px 4px",border:"none",background:"transparent",
              color:tab===key?C.teal:C.textLight,fontFamily:"'DM Sans',sans-serif",
              fontWeight:tab===key?700:400,fontSize:13,cursor:"pointer",
              borderBottom:tab===key?`2px solid ${C.teal}`:"2px solid transparent",transition:"all 0.2s"
            }}>{label}</button>
          ))}
        </div>
      </div>

      {tab==="queue" && (
        <div className="scroll">
          {/* FILTERS */}
          <div style={{padding:"12px 14px 8px",display:"flex",gap:6,overflowX:"auto"}}>
            {["all","urgent","moderate","low","pending","in-progress","complete"].map(f=>(
              <button key={f} onClick={()=>setFilter(f)} style={{
                flexShrink:0,padding:"5px 12px",borderRadius:100,border:`1.5px solid ${filter===f?C.teal:C.border}`,
                background:filter===f?C.teal:"transparent",color:filter===f?C.navy:C.textMid,
                fontSize:11,fontWeight:600,cursor:"pointer",textTransform:"capitalize",transition:"all 0.15s"
              }}>{f.replace("-"," ")}</button>
            ))}
          </div>
          {/* CASE LIST */}
          <div style={{padding:"0 14px 20px",display:"flex",flexDirection:"column",gap:8}}>
            {sorted.map((c,i)=>(
              <div key={c.id} className="fu" style={{animationDelay:`${i*0.04}s`}}>
                <Card onClick={()=>onCase(c,setCases)}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
                    <div style={{fontFamily:"'Bitter',serif",fontSize:15,fontWeight:700,color:C.navy}}>
                      {c.name} <span style={{fontFamily:"'DM Sans',sans-serif",fontSize:12,fontWeight:400,color:C.textMid}}>· {c.age}yo</span>
                    </div>
                    <UrgencyPill urgency={c.urgency} small/>
                  </div>
                  <div style={{fontSize:12,color:C.textMid,lineHeight:1.55,marginBottom:8}}>{c.concern}</div>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <StatusPill status={c.status}/>
                    <span style={{fontSize:10,color:C.textLight}}>{c.time}</span>
                  </div>
                </Card>
              </div>
            ))}
            {sorted.length===0 && <div style={{textAlign:"center",padding:"40px 0",color:C.textLight,fontSize:14}}>No cases match this filter.</div>}
          </div>
        </div>
      )}

      {tab==="insights" && (
        <div className="scroll" style={{padding:"16px 14px"}}>
          {/* URGENCY BAR */}
          <Card style={{marginBottom:12}}>
            <div style={{fontSize:11,fontWeight:700,color:C.textLight,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:12}}>Urgency Distribution</div>
            {["urgent","moderate","low"].map(u=>{
              const cnt = cases.filter(c=>c.urgency===u).length;
              const pct = Math.round((cnt/cases.length)*100);
              const uc = URGENCY[u];
              return (
                <div key={u} style={{marginBottom:10}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                    <span style={{fontSize:12,fontWeight:600,color:uc.color,textTransform:"capitalize"}}>{uc.icon} {u}</span>
                    <span style={{fontSize:12,color:C.textMid}}>{cnt} ({pct}%)</span>
                  </div>
                  <div style={{height:6,background:C.surface,borderRadius:3}}>
                    <div style={{height:"100%",background:uc.color,borderRadius:3,width:`${pct}%`,transition:"width 0.8s ease"}}/>
                  </div>
                </div>
              );
            })}
          </Card>
          <Card style={{marginBottom:12}}>
            <div style={{fontSize:11,fontWeight:700,color:C.textLight,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:10}}>Performance</div>
            {[["Avg AI response","< 30 sec"],["Avg callback time","18 min"],["Follow-up sent","80%"],["Cases resolved","60%"]].map(([k,v])=>(
              <div key={k} style={{display:"flex",justifyContent:"space-between",padding:"7px 0",borderBottom:`1px solid ${C.border}`}}>
                <span style={{fontSize:13,color:C.textMid}}>{k}</span>
                <span style={{fontSize:13,fontWeight:700,color:C.teal}}>{v}</span>
              </div>
            ))}
          </Card>
          <Card>
            <div style={{fontSize:11,fontWeight:700,color:C.textLight,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:10}}>Top Concerns Today</div>
            {[["Fever / Infection",3],["GI / Vomiting",2],["Respiratory",1],["Rash / Skin",1]].map(([k,v])=>(
              <div key={k} style={{display:"flex",justifyContent:"space-between",padding:"7px 0",borderBottom:`1px solid ${C.border}`}}>
                <span style={{fontSize:13,color:C.navy}}>{k}</span>
                <span style={{fontSize:13,fontWeight:700,color:C.urgent}}>{v}</span>
              </div>
            ))}
          </Card>
        </div>
      )}
    </div>
  );
}

// ─── SCREEN 8: CASE DETAIL ───────────────────────────────────────
function CaseDetail({ c, onBack, onUpdate }) {
  const [caseData, setCaseData] = useState(c);
  const [aiNote, setAiNote] = useState("");
  const [aiNoteLoading, setAiNoteLoading] = useState(false);
  const [followUp, setFollowUp] = useState("");
  const [followUpLoading, setFollowUpLoading] = useState(false);
  const [followUpQueued, setFollowUpQueued] = useState(false);

  function updateStatus(status) {
    const updated = { ...caseData, status };
    setCaseData(updated);
    onUpdate(updated);
  }

  async function generateNote() {
    setAiNoteLoading(true);
    try {
      const note = await callClaude(
        "You are a pediatric triage nurse writing a brief clinical handoff note for a colleague. Be concise, professional. 3-4 sentences. Do not diagnose. Use nursing language.",
        `Child: ${caseData.age}yo. Concern: ${caseData.concern}. Urgency: ${caseData.urgency}. Scale: ${caseData.scale}/10. Write the handoff note.`,
        200
      );
      setAiNote(note);
    } catch { setAiNote("Unable to generate note at this time."); }
    setAiNoteLoading(false);
  }

  async function generateFollowUp() {
    setFollowUpLoading(true);
    try {
      const sms = await callClaude(
        "Write a warm 1-2 sentence SMS follow-up from a pediatric nurse line. Under 160 chars. Caring, not clinical. Include 💚. No diagnosis.",
        `Child concern: ${caseData.concern}. Age: ${caseData.age}. Generate the follow-up SMS.`,
        120
      );
      setFollowUp(sms);
    } catch { setFollowUp(`Hi! Following up on your child — how are things going today? 💚 Reply anytime.`); }
    setFollowUpLoading(false);
  }

  function queueFollowUp() { setFollowUpQueued(true); }

  return (
    <div className="screen" style={{background:C.offWhite}}>
      <TopBar
        title={`${caseData.name} · ${caseData.id}`}
        subtitle={`${caseData.age}yo · ${caseData.time}`}
        onBack={onBack}
        light
        action={<UrgencyPill urgency={caseData.urgency} small/>}
      />
      <div className="scroll" style={{padding:"14px 14px 32px",display:"flex",flexDirection:"column",gap:12}}>

        {/* CONCERN */}
        <Card className="fu">
          <div style={{fontSize:10,fontWeight:700,color:C.textLight,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:6}}>Chief Concern</div>
          <div style={{fontSize:14,color:C.navy,lineHeight:1.65,marginBottom:10}}>{caseData.concern}</div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            <div style={{background:C.surface,borderRadius:8,padding:"6px 10px"}}>
              <div style={{fontSize:10,color:C.textLight}}>Phone</div>
              <div style={{fontSize:12,fontWeight:600,color:C.navy}}>{caseData.phone}</div>
            </div>
            <div style={{background:C.surface,borderRadius:8,padding:"6px 10px"}}>
              <div style={{fontSize:10,color:C.textLight}}>Worry Scale</div>
              <div style={{fontSize:12,fontWeight:600,color:C.navy}}>{caseData.scale}/10</div>
            </div>
            <div style={{background:C.surface,borderRadius:8,padding:"6px 10px"}}>
              <div style={{fontSize:10,color:C.textLight}}>Status</div>
              <StatusPill status={caseData.status}/>
            </div>
          </div>
        </Card>

        {/* UPDATE STATUS */}
        <Card className="fu" style={{animationDelay:"0.05s"}}>
          <div style={{fontSize:10,fontWeight:700,color:C.textLight,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:8}}>Update Status</div>
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            {["pending","in-progress","complete","escalated"].map(s=>(
              <button key={s} onClick={()=>updateStatus(s)} style={{
                padding:"6px 12px",borderRadius:100,fontSize:11,fontWeight:600,cursor:"pointer",
                border:`1.5px solid ${caseData.status===s?C.teal:C.border}`,
                background:caseData.status===s?C.teal:"transparent",
                color:caseData.status===s?C.navy:C.textMid,
                textTransform:"capitalize",transition:"all 0.15s"
              }}>{s.replace("-"," ")}</button>
            ))}
          </div>
        </Card>

        {/* AI NURSE NOTE */}
        <Card className="fu" style={{animationDelay:"0.1s"}}>
          <div style={{fontSize:10,fontWeight:700,color:C.textLight,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:8}}>AI Nurse Handoff Note</div>
          {aiNote ? (
            <div style={{background:"#F0FDF9",borderRadius:8,padding:"10px 12px",fontSize:13,color:C.navy,lineHeight:1.65,border:`1px solid ${C.low}22`}}>
              {aiNote}
            </div>
          ) : (
            <Btn variant="ghost" onClick={generateNote} disabled={aiNoteLoading} style={{width:"100%"}}>
              {aiNoteLoading ? <><Spinner size={14}/> Generating...</> : "⚡ Generate AI Nurse Note"}
            </Btn>
          )}
        </Card>

        {/* SMS FOLLOW-UP */}
        <Card className="fu" style={{animationDelay:"0.15s"}}>
          <div style={{fontSize:10,fontWeight:700,color:C.textLight,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:8}}>SMS Follow-Up</div>
          {followUpQueued ? (
            <div style={{background:"#F0FDF9",borderRadius:8,padding:"10px 12px",border:`1px solid ${C.low}33`}}>
              <div style={{fontSize:10,fontWeight:700,color:C.low,marginBottom:4}}>✓ QUEUED — SENDS IN 24 HRS</div>
              <div style={{fontSize:12,color:C.navy,fontStyle:"italic"}}>{followUp}</div>
            </div>
          ) : followUp ? (
            <div>
              <div style={{background:"#FFFBEB",borderRadius:8,padding:"10px 12px",fontSize:13,color:C.navy,fontStyle:"italic",marginBottom:8,border:`1px solid ${C.moderate}22`}}>
                "{followUp}"
              </div>
              <Btn variant="primary" small onClick={queueFollowUp} style={{width:"100%"}}>
                ✓ Queue This Follow-Up
              </Btn>
            </div>
          ) : (
            <Btn variant="outline" onClick={generateFollowUp} disabled={followUpLoading} style={{width:"100%"}}>
              {followUpLoading ? <><Spinner size={14}/> Generating...</> : "💬 Generate Follow-Up SMS"}
            </Btn>
          )}
        </Card>

        {/* ACTIONS */}
        <div className="fu" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,animationDelay:"0.2s"}}>
          <a href={`tel:${caseData.phone}`} style={{textDecoration:"none"}}>
            <Btn variant="dark" style={{width:"100%",borderRadius:12}}>📞 Call Back</Btn>
          </a>
          <a href={`sms:${caseData.phone}`} style={{textDecoration:"none"}}>
            <Btn variant="outline" style={{width:"100%",borderRadius:12}}>💬 Send SMS</Btn>
          </a>
        </div>
      </div>
    </div>
  );
}

// ─── NURSE PIN GATE ───────────────────────────────────────────────
function NursePinGate({ onSuccess, onBack }) {
  const [pin, setPin] = useState("");
  const [error, setError] = useState(false);
  const [shake, setShake] = useState(false);

  function tryPin() {
    if (pin === "1234") { onSuccess(); }
    else {
      setError(true); setShake(true); setPin("");
      setTimeout(()=>setShake(false), 600);
    }
  }

  return (
    <div className="screen" style={{background:C.navy,justifyContent:"center",alignItems:"center",padding:32}}>
      <div style={{position:"absolute",top:"20%",left:"50%",transform:"translateX(-50%)",width:200,height:200,borderRadius:"50%",background:C.tealGlow,filter:"blur(50px)",pointerEvents:"none"}}/>
      <div className="fu" style={{textAlign:"center",width:"100%",maxWidth:320,position:"relative"}}>
        <NurseAvatar size={56}/>
        <div style={{fontFamily:"'Bitter',serif",fontSize:24,fontWeight:700,color:C.white,marginTop:16,marginBottom:6}}>Nurse Access</div>
        <div style={{fontSize:13,color:C.textLight,marginBottom:32}}>Enter your PIN to continue<br/><span style={{color:C.teal,fontSize:11}}>(Demo PIN: 1234)</span></div>
        <div style={{
          display:"flex",gap:14,justifyContent:"center",marginBottom:24,
          animation:shake?"shake 0.5s ease":"none"
        }}>
          {[0,1,2,3].map(i=>(
            <div key={i} style={{
              width:16,height:16,borderRadius:"50%",
              background:i<pin.length?C.teal:"transparent",
              border:`2px solid ${i<pin.length?C.teal:C.textLight}`,
              transition:"all 0.15s"
            }}/>
          ))}
        </div>
        {error && <div style={{color:C.urgent,fontSize:12,marginBottom:16}}>Incorrect PIN. Try again.</div>}
        <input
          type="password" inputMode="numeric" maxLength={4}
          value={pin} onChange={e=>{ setPin(e.target.value); setError(false); }}
          onKeyDown={e=>e.key==="Enter"&&tryPin()}
          style={{
            width:"100%",background:"rgba(255,255,255,0.07)",border:`1.5px solid ${error?C.urgent:C.borderLight}`,
            borderRadius:12,padding:"14px",fontFamily:"'DM Sans',sans-serif",fontSize:24,
            textAlign:"center",letterSpacing:12,color:C.white,outline:"none",marginBottom:16
          }}
          placeholder="••••"
        />
        <Btn onClick={tryPin} style={{width:"100%",marginBottom:12}}>Enter Dashboard →</Btn>
        <Btn variant="outlineW" onClick={onBack} style={{width:"100%"}}>← Back</Btn>
      </div>
    </div>
  );
}

// ─── ROOT APP ────────────────────────────────────────────────────
export default function App() {
  const [screen, setScreen] = useState("splash");
  const [, setRole] = useState(null);
  const [intake, setIntake] = useState(null);
  const [triageResult, setTriageResult] = useState(null);
  const [membership, setMembership] = useState(false);
  const [showMembership, setShowMembership] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [, setCases] = useState(MOCK_CASES);

  function handleRole(r) {
    setRole(r);
    if (r === "parent") setScreen("home");
    else setScreen("pingate");
  }

  function handleCheckerComplete(answers) {
    setIntake(answers);
    setScreen("chat");
  }

  async function handleTriageComplete(result) {
    setTriageResult(result);
    try {
      await addDoc(collection(db, "cases"), {
        ...result,
        createdAt: new Date().toISOString(),
        status: "pending"
      });
    } catch(e) {
      console.error("Error saving case:", e);
    }
    setScreen("confirm");
  }

  function handleCaseSelect(c, setCasesRef) {
    setSelectedCase(c);
    setScreen("casedetail");
  }

  function handleCaseUpdate(updated) {
    setCases(cs => cs.map(c => c.id === updated.id ? updated : c));
    setSelectedCase(updated);
  }

  function handleJoin(planId) {
    if (planId === "member") setMembership(true);
    setShowMembership(false);
  }

  const screens = {
    splash:     <SplashScreen onRole={handleRole}/>,
    home:       <HomeScreen onNav={s=>setScreen(s)} membership={membership} onUpgrade={()=>setShowMembership(true)}/>,
    checker:    <SymptomChecker onComplete={handleCheckerComplete} onBack={()=>setScreen("home")}/>,
    chat:       <TriageChat intake={intake} onComplete={handleTriageComplete} onBack={()=>setScreen("checker")}/>,
    confirm:    <ConfirmationScreen result={triageResult||{}} onHome={()=>setScreen("home")} onBack={()=>setScreen("chat")}/>,
    pingate:    <NursePinGate onSuccess={()=>setScreen("nursedash")} onBack={()=>setScreen("splash")}/>,
    nursedash:  <NurseDashboard onCase={handleCaseSelect} onLogout={()=>setScreen("splash")}/>,
    casedetail: <CaseDetail c={selectedCase} onBack={()=>setScreen("nursedash")} onUpdate={handleCaseUpdate}/>,
  };

  return (
    <div style={{maxWidth:430,margin:"0 auto",height:"100vh",position:"relative",overflow:"hidden",boxShadow:"0 0 80px rgba(0,0,0,0.3)"}}>
      <style>{GLOBAL_CSS}</style>
      {screens[screen] || screens.splash}
      {showMembership && <MembershipModal onClose={()=>setShowMembership(false)} onJoin={handleJoin}/>}
    </div>
  );
}
